// use-web-push — subscribe / unsubscribe Web Push (VAPID) и регистрация
// токена на сервере через push.register tRPC mutation.
//
// На Capacitor этот hook отключён (`__IS_CAPACITOR__`) — нативный плагин
// (FCM) делает регистрацию через packages/platform/push.
import { useCallback, useEffect, useState } from "react";
import { getOrCreateWebDeviceId } from "@/lib/device-fingerprint";

declare const __IS_CAPACITOR__: boolean;

export interface WebPushState {
	/** Поддерживает ли браузер Web Push (Service Worker + PushManager). */
	supported: boolean;
	/** Активная subscription, если есть. */
	subscription: PushSubscription | null;
	/** В процессе subscribe/unsubscribe. */
	loading: boolean;
	/** Последняя ошибка. */
	error: Error | null;
}

export interface UseWebPushOptions {
	/** VAPID public key (base64url) — приходит с сервера через config endpoint. */
	vapidPublicKey: string | null;
	/**
	 * Callback после успешной subscription — обычно отправляет на сервер через
	 * tRPC `push.register`. Принимает endpoint + keys (раскодированные base64).
	 */
	onSubscribe: (input: {
		deviceId: string;
		endpoint: string;
		p256dh: string;
		auth: string;
		locale?: string;
		timezone?: string;
	}) => Promise<void>;
	/** Callback после unsubscribe — обычно `push.unregister`. */
	onUnsubscribe: (input: { endpoint: string }) => Promise<void>;
}

function arrayBufferToBase64Url(buf: ArrayBuffer): string {
	let binary = "";
	const bytes = new Uint8Array(buf);
	for (const b of bytes) binary += String.fromCharCode(b);
	return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlToUint8Array(base64: string): Uint8Array {
	const padding = "=".repeat((4 - (base64.length % 4)) % 4);
	const std = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
	const raw = atob(std);
	const out = new Uint8Array(raw.length);
	for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
	return out;
}

const isSupported = (): boolean => {
	if (
		typeof window === "undefined" ||
		(typeof __IS_CAPACITOR__ !== "undefined" && __IS_CAPACITOR__)
	) {
		return false;
	}
	return "serviceWorker" in navigator && "PushManager" in window;
};

export function useWebPush(opts: UseWebPushOptions): WebPushState & {
	subscribe: () => Promise<void>;
	unsubscribe: () => Promise<void>;
} {
	const [supported] = useState(isSupported);
	const [subscription, setSubscription] = useState<PushSubscription | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	// Загружаем текущую subscription при mount.
	useEffect(() => {
		if (!supported) return;
		void navigator.serviceWorker.ready
			.then((reg) => reg.pushManager.getSubscription())
			.then(setSubscription)
			.catch(() => setSubscription(null));
	}, [supported]);

	// subscribe/unsubscribe бросают ошибку наружу (после setError), чтобы
	// вызывающий (use-web-push-channel) мог детектировать успех/провал —
	// иначе тумблер не знает, выдал ли браузер разрешение.
	const subscribe = useCallback(async () => {
		if (!supported || !opts.vapidPublicKey) {
			const err = new Error(supported ? "VAPID public key missing" : "web-push not supported");
			setError(err);
			throw err;
		}
		setLoading(true);
		setError(null);
		try {
			// SW регистрируется в main.tsx только в PROD — гарантируем наличие
			// активной регистрации перед subscribe (иначе `ready` не зарезолвится).
			const existing = await navigator.serviceWorker.getRegistration();
			if (!existing) await navigator.serviceWorker.register("/sw.js");
			const reg = await navigator.serviceWorker.ready;
			const keyBytes = base64UrlToUint8Array(opts.vapidPublicKey);
			const sub = await reg.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: keyBytes.buffer.slice(
					keyBytes.byteOffset,
					keyBytes.byteOffset + keyBytes.byteLength,
				) as ArrayBuffer,
			});
			const p256dh = sub.getKey("p256dh");
			const auth = sub.getKey("auth");
			if (!p256dh || !auth) throw new Error("Web Push keys missing in subscription");
			const deviceId = await getOrCreateWebDeviceId();
			await opts.onSubscribe({
				deviceId,
				endpoint: sub.endpoint,
				p256dh: arrayBufferToBase64Url(p256dh),
				auth: arrayBufferToBase64Url(auth),
				locale: navigator.language?.slice(0, 2),
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});
			setSubscription(sub);
		} catch (err) {
			setError(err as Error);
			throw err;
		} finally {
			setLoading(false);
		}
	}, [supported, opts]);

	const unsubscribe = useCallback(async () => {
		if (!subscription) return;
		setLoading(true);
		setError(null);
		try {
			const endpoint = subscription.endpoint;
			await subscription.unsubscribe();
			await opts.onUnsubscribe({ endpoint });
			setSubscription(null);
		} catch (err) {
			setError(err as Error);
			throw err;
		} finally {
			setLoading(false);
		}
	}, [subscription, opts]);

	return { supported, subscription, loading, error, subscribe, unsubscribe };
}
